/**
 * Billing Snapshot — localStorage cache written exclusively from live /subscription API responses.
 * Provides instant, flicker-free tier display on mount while the live API is in-flight.
 */

export interface BillingSnapshot {
  user_id: string;
  subscription_tier: string;
  billing_source: string | null;
  entitlement_expires_at: string | null;
  is_ai_rate_limited: boolean;
  provider: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  updated_at: string;
}

export interface SubscriptionData {
  subscription_tier: string;
  billing_source: string | null;
  entitlement_expires_at: string | null;
  is_ai_rate_limited: boolean;
  provider: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

const STORAGE_KEY = 'filmple:billing_snapshot';

/** Read snapshot from localStorage. Returns null if missing, corrupt, or wrong user. */
export function readBillingSnapshot(userId: string | undefined | null): BillingSnapshot | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const snap: BillingSnapshot = JSON.parse(raw);
    // Cross-account guard
    if (snap.user_id !== userId) return null;
    return snap;
  } catch {
    return null;
  }
}

/**
 * Merge data into the billing snapshot for the given user.
 * If onTierChange is provided and the written tier differs from the previous, invokes callback.
 */
export function writeBillingSnapshot(
  userId: string | undefined | null,
  data: Partial<BillingSnapshot>,
  onTierChange?: () => void,
): void {
  if (!userId) return;
  try {
    const existing = readBillingSnapshot(userId);
    const merged = { ...existing, ...data, user_id: userId, updated_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    if (onTierChange && data.subscription_tier && data.subscription_tier !== existing?.subscription_tier) {
      onTierChange();
    }
  } catch { /* silent */ }
}

/** Remove billing snapshot from localStorage (call on sign-out). */
export function clearBillingSnapshot(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
}

/** Convert a BillingSnapshot to SubscriptionData with safe defaults for missing fields. */
export function snapshotToSubscriptionData(snap: BillingSnapshot): SubscriptionData {
  return {
    subscription_tier: snap.subscription_tier,
    billing_source: snap.billing_source ?? null,
    entitlement_expires_at: snap.entitlement_expires_at ?? null,
    is_ai_rate_limited: snap.is_ai_rate_limited ?? false,
    provider: snap.provider ?? null,
    status: snap.status ?? null,
    current_period_end: snap.current_period_end ?? null,
    cancel_at_period_end: snap.cancel_at_period_end ?? false,
  };
}
